import {
  FocusEvent,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';

import { cn } from '@components/primitives/utilities';

import { RadioGroupContext } from './context';
import { RadioGroupDescription } from './RadioGroupDescription';
import { RadioGroupIcon } from './RadioGroupIcon';
import { RadioGroupIndicator } from './RadioGroupIndicator';
import { RadioGroupItem } from './RadioGroupItem';
import { RadioGroupLabel } from './RadioGroupLabel';

import css from './styles.module.css';

export interface RadioGroupProps<T extends string> {
  children: ReactNode;
  className?: string;
  name?: string;
  value?: T | null | undefined;
  defaultValue?: T;
  onChange?: (value: T) => void;
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  columns?: number;
  disabled?: boolean;
  loop?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps<string>>(
  function RadioGroup(
    {
      children,
      className,
      name: propsName,
      value,
      defaultValue,
      onChange,
      onBlur,
      columns: manualColumns,
      disabled = false,
      loop,
      orientation,
    },
    ref
  ) {
    const internalName = useId();
    const name = propsName ?? internalName;

    const groupRef = useRef<HTMLDivElement>(null);

    const [items, setItems] = useState<Record<string, boolean>>({});

    const registerItem = useCallback((id: string) => {
      // Descriptions mount before their item (child effects run first), so
      // never overwrite an entry that is already there.
      setItems((current) =>
        id in current ? current : { ...current, [id]: false }
      );
      return () =>
        setItems((current) => {
          const { [id]: removed, ...rest } = current;
          return removed === undefined ? current : rest;
        });
    }, []);

    const registerDescription = useCallback((id: string) => {
      setItems((current) => ({ ...current, [id]: true }));
      return () =>
        setItems((current) =>
          current[id] === true ? { ...current, [id]: false } : current
        );
    }, []);

    const columns = useMemo(() => {
      if (manualColumns) return manualColumns;

      const descriptions = Object.values(items);
      const count = descriptions.length;
      const hasDescription = descriptions.some(Boolean);

      if (count <= 1) {
        return 1;
      } else if (count % 3 === 0) {
        if (count % 2 === 0) {
          return 2;
        } else if (hasDescription) {
          return 1;
        } else {
          return 3;
        }
      } else {
        return 2;
      }
    }, [manualColumns, items]);

    // Radix fires blur per item, so synthesise one group-level blur for forms.
    // relatedTarget is where focus is going; activeElement is still stale here.
    const handleBlur = useCallback(
      (event: FocusEvent<HTMLDivElement>) => {
        const next = event.relatedTarget as Node | null;
        if (!next || !groupRef.current?.contains(next)) {
          onBlur?.(event);
        }
      },
      [onBlur]
    );

    const context = useMemo(
      () => ({ registerItem, registerDescription }),
      [registerItem, registerDescription]
    );

    return (
      <RadioGroupContext.Provider value={context}>
        <RadioGroupPrimitive.Root
          ref={composeRefs(ref, groupRef)}
          name={name}
          // `null` and `''` both mean "nothing selected", not "uncontrolled".
          value={value === undefined ? undefined : (value ?? '')}
          defaultValue={defaultValue}
          onValueChange={onChange}
          disabled={disabled}
          loop={loop}
          orientation={orientation}
          className={cn(css.group, className)}
          style={{ '--columns': columns }}
          onBlurCapture={handleBlur}
        >
          {children}
        </RadioGroupPrimitive.Root>
      </RadioGroupContext.Provider>
    );
  }
) as <T extends string>(
  props: RadioGroupProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => JSX.Element;

const RadioGroupNamespace = Object.assign(RadioGroup, {
  Item: RadioGroupItem,
  Indicator: RadioGroupIndicator,
  Icon: RadioGroupIcon,
  Label: RadioGroupLabel,
  Description: RadioGroupDescription,
});

export { RadioGroupNamespace as RadioGroup };

export type { RadioGroupDescriptionProps } from './RadioGroupDescription';
export type { RadioGroupIconProps } from './RadioGroupIcon';
export type { RadioGroupIndicatorProps } from './RadioGroupIndicator';
export type { RadioItemProps } from './RadioGroupItem';
export type { RadioGroupLabelProps } from './RadioGroupLabel';
